import { Input, Stack, SegmentedControl } from '@mantine/core';
import StyledTextInput from '@/components/StyledInputFields/StyledTextInput.tsx';
import DataInputSelectorUI from '@/pages/Editor/components/SharedFields/DataInputSelector/DataInputSelectorUI.tsx';
import { useUpdateStepFields } from '@/hooks/api/steps/useUpdateStepFields.ts';
import { DataInput, RuntimeOutputName, RuntimeOutputNameSource } from '@/types/automationFields.ts';
import { AutomationStep } from '@/types/automationSteps.ts';
import { StepErrorType } from '@/types/stepErrorTypes.ts';
import useStepSpecificError from '@/hooks/useStepSpecificError.ts';
import { DEFAULT_DYNAMIC_DATA_INPUT, STEP_FIELD_MAX_LENGTH } from '@/utils/constants.ts';

type RuntimeOutputNameMode = 'default' | RuntimeOutputNameSource;

type RuntimeOutputNameInputProps = {
  step: AutomationStep;
  value?: RuntimeOutputName | null;
  onChange: (runtimeOutputName: RuntimeOutputName | null) => void;
  onChangeNoDelay: (runtimeOutputName: RuntimeOutputName | null) => void;
  error?: string;
};

type RuntimeOutputNameFields = {
  runtime_output_name?: RuntimeOutputName | null;
};

const makeDefaultDataInput = (): DataInput => ({
  ...DEFAULT_DYNAMIC_DATA_INPUT,
  config: { ...DEFAULT_DYNAMIC_DATA_INPUT.config },
});

const makeRuntimeOutputName = (
  source: RuntimeOutputNameSource,
  previous?: RuntimeOutputName | null,
): RuntimeOutputName => ({
  source,
  static_value: previous?.static_value ?? '',
  data_input: previous?.data_input ?? makeDefaultDataInput(),
});

export const RuntimeOutputNameInput = ({
  step,
  value,
  onChange,
  onChangeNoDelay,
  error,
}: RuntimeOutputNameInputProps) => {
  const mode: RuntimeOutputNameMode = value?.source ?? 'default';

  return (
    <Input.Wrapper label="Runtime Output Name">
      <Stack gap="xs">
        <SegmentedControl
          value={mode}
          onChange={(nextMode) => {
            if (nextMode === 'default') {
              onChangeNoDelay(null);
              return;
            }
            onChangeNoDelay(makeRuntimeOutputName(nextMode as RuntimeOutputNameSource, value));
          }}
          data={[
            { label: 'Default', value: 'default' },
            { label: 'Static', value: RuntimeOutputNameSource.Static },
            { label: 'From data', value: RuntimeOutputNameSource.DataInput },
          ]}
          size="xs"
          fullWidth
        />

        {value?.source === RuntimeOutputNameSource.Static && (
          <StyledTextInput
            label="Name"
            value={value.static_value}
            placeholder="Output name"
            onChange={(event) => {
              const staticValue = event.currentTarget.value.replace(/[/\n]/g, '');
              onChange({ ...value, static_value: staticValue });
            }}
            required={false}
            maxLength={STEP_FIELD_MAX_LENGTH}
            error={error}
          />
        )}

        {value?.source === RuntimeOutputNameSource.DataInput && (
          <DataInputSelectorUI
            dataInput={value.data_input}
            stepId={step.step_id}
            updateDataInput={(dataInput) => onChangeNoDelay({ ...value, data_input: dataInput })}
            label="Data"
            required={true}
            error={error}
          />
        )}
      </Stack>
    </Input.Wrapper>
  );
};

const RuntimeOutputNameField = ({ step }: { step: AutomationStep }) => {
  const { fields, onChange, onChangeNoDelay } = useUpdateStepFields<RuntimeOutputNameFields>({
    step,
  });
  const error = useStepSpecificError(step, StepErrorType.InvalidRuntimeOutputName);

  return (
    <RuntimeOutputNameInput
      step={step}
      value={fields.runtime_output_name ?? null}
      onChange={(runtimeOutputName) => onChange({ runtime_output_name: runtimeOutputName })}
      onChangeNoDelay={(runtimeOutputName) =>
        onChangeNoDelay({ runtime_output_name: runtimeOutputName })
      }
      error={error}
    />
  );
};

export default RuntimeOutputNameField;
